import { CSSWithMultiValues } from '@chakra-ui/styled-system';
import { Box, Text, Heading, theme, Flex, Image } from '@everlywell/ui-kit';
import { GatsbyImage as Img } from 'gatsby-plugin-image';
import React from 'react';
import renderRichText, {
  ContentfulRichText,
} from 'utils/contentful/renderRichText';
import { IGatsbyImage } from 'utils/types';

// Types ----------

export type Card = {
  desktopImage: IGatsbyImage;
  mobileImage: IGatsbyImage;
  title: string;
  description: ContentfulRichText;
  stepNumber?: string | null;
};

export interface StepCardProps {
  card: Card;
  imageStyles?: CSSWithMultiValues;
  index: number;
  isLastCard?: boolean;
}

// StepCard ------

const StepCard: React.FC<StepCardProps> = (props) => {
  const { card, imageStyles, index, isLastCard } = props;
  const { desktopImage, mobileImage, title, description } = card;

  return (
    <Flex
      flexDirection={{
        base: 'row',
        largeTablet: 'column',
      }}
      gap={6}
      height="100%"
    >
      <Flex
        alignItems="center"
        gap={3}
        flexDirection={{
          base: 'column',
          largeTablet: 'row',
        }}
      >
        <Flex
          minWidth="35px"
          paddingY={1}
          background="tints.lightGrey"
          borderRadius="6px"
          alignItems="center"
          justifyContent={'center'}
        >
          <Text
            size="lg"
            paddingX={card.stepNumber ? 2 : 0}
            paddingY={card.stepNumber ? 0.5 : 0}
          >
            {card.stepNumber ? card.stepNumber : index + 1}
          </Text>
        </Flex>
        {!isLastCard && (
          <Box
            width={{
              base: '1px',
              largeTablet: '100%',
            }}
            height={{
              base: '100%',
              largeTablet: '1px',
            }}
            sx={{
              backgroundImage: {
                base: `linear-gradient(to bottom, ${theme.colors.tints.lightGrey}, ${theme.colors.tints.white} 100%)`,
                largeTablet: `linear-gradient(to right, ${theme.colors.tints.lightGrey}, ${theme.colors.tints.white} 100%)`,
              },
            }}
          />
        )}
      </Flex>
      <Flex flexDirection="column" justifyContent="space-between" height="100%">
        <Box>
          <Heading size="sm">{title}</Heading>
          <Text
            as="div"
            size="lg"
            marginTop={2}
            color="tints.darkGrey"
            sx={{
              alignSelf: 'stretch',
            }}
          >
            {renderRichText(description)}
          </Text>
        </Box>
        {/* Image aspect ratio to be 3:2 from Figma design */}
        <Box borderRadius={'xl'} display="flex" marginTop={4} width="100%">
          {/* Image as a component for screen readers */}

          <Image
            display={['block', null, 'none']}
            borderRadius={'xl'}
            aspectRatio={'1.5'}
            as={Img}
            image={mobileImage.gatsbyImageData}
            alt={mobileImage.description || ''}
            objectFit="fill"
            sx={imageStyles}
            data-testid="step-card-image-mobile"
          />
          <Image
            display={['none', null, 'block']}
            borderRadius={'xl'}
            aspectRatio={'1.5'}
            as={Img}
            image={desktopImage.gatsbyImageData}
            alt={desktopImage.description || ''}
            objectFit="fill"
            sx={imageStyles}
            data-testid="step-card-image-desktop"
          />
        </Box>
      </Flex>
    </Flex>
  );
};

// Export ---------

export default React.memo(StepCard);
